import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import type { TabsValue } from '@mantine/core';
import {
	Box,
	Center,
	Popover,
	Space,
	Tabs,
	// eslint-disable-next-line no-restricted-imports
	Text,
	UnstyledButton,
	createStyles,
	useMantineColorScheme,
} from '@mantine/core';
import { Button } from '@repo/foundations';
import { Icon } from '@repo/foundations/components/Icon/Icon';
import type { IconNames } from '@repo/foundations/components/Icon/types';
import { memo, useCallback, useState } from 'react';
import { EntityType } from '../../enums/entityType';
import { IconSelector } from '../IconSelector/IconSelector';

import type { IconEmojiSelectorValue } from './utils';
import {
	iconEmojiSelectorValueToString,
	isEmoji,
	isTablerIcon,
	stringToIconEmojiSelectorValue,
} from './utils';

export interface IconEmojiSelectorProps {
	value?: string;
	onChange: (value: string) => void;
	readOnly?: boolean;
	iconSize?: number;
	emojiSize?: number;
	entityType?: EntityType | 'automation';
	defaultOpened?: boolean;
}

const useStyles = createStyles(
	(
		theme,
		{
			iconSize,
			emojiSize,
			selectedIconColor,
		}: { iconSize: number; emojiSize: number; selectedIconColor: string }
	) => ({
		center: {
			minWidth: iconSize,
			minHeight: iconSize,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
		},
		emoji: {
			fontSize: emojiSize,
			display: 'inline-block',
		},
		popoverDropdown: {
			boxShadow: theme.shadows.md,
			borderRadius: theme.radius.md,
		},
		icon: {
			color: selectedIconColor,
			width: iconSize,
			height: iconSize,
			position: 'relative',
		},
		removeButton: {
			alignSelf: 'center',
		},
		spacer: {
			flexGrow: 1,
		},
	})
);

function IconEmojiSelector({
	value = '💼',
	onChange,
	readOnly = false,
	iconSize = 32,
	emojiSize = 28,
	entityType = EntityType.document,
	defaultOpened = false,
}: IconEmojiSelectorProps) {
	const [activeTab, setActiveTab] = useState<TabsValue>('emoji');
	const [emojiIconValue, setEmojiIconValue] = useState<IconEmojiSelectorValue>(
		stringToIconEmojiSelectorValue(value)
	);
	const { colorScheme } = useMantineColorScheme();

	const handleEmojiSelect = useCallback(
		(selectedEmoji: { native: string }) => {
			setEmojiIconValue({ emoji: selectedEmoji.native });
			onChange(iconEmojiSelectorValueToString({ emoji: selectedEmoji.native }));
		},
		[onChange]
	);

	const handleIconSelect = useCallback(
		({ iconName, iconColor }: { iconName: IconNames; iconColor: string }) => {
			setEmojiIconValue({ iconName, iconColor });
			onChange(iconEmojiSelectorValueToString({ iconName, iconColor }));
		},
		[onChange]
	);

	const handleReset = useCallback(() => {
		const defaultIcon = (() => {
			switch (entityType) {
				case EntityType.collection:
					return 'folder';
				case EntityType.document:
					return 'file';
				case EntityType.dictionary_term:
				case EntityType.glossary:
					return 'book';
				case EntityType.bi_dashboard:
					return 'customDashboard';
				case 'automation':
					return 'bolt';
				default:
					return 'file';
			}
		})();
		setEmojiIconValue({ iconName: defaultIcon, iconColor: '#4a4a4a' });
		onChange(
			iconEmojiSelectorValueToString({
				iconName: defaultIcon,
				iconColor: '#4a4a4a',
			})
		);
	}, [entityType, onChange]);

	const selectedEmoji = isEmoji(emojiIconValue) ? emojiIconValue.emoji : null;
	const selectedIcon: IconNames = isTablerIcon(emojiIconValue)
		? emojiIconValue.iconName
		: 'briefcase';
	const selectedIconColor =
		isTablerIcon(emojiIconValue) && emojiIconValue.iconColor
			? emojiIconValue.iconColor
			: '#4a4a4a';

	const { classes } = useStyles({ iconSize, emojiSize, selectedIconColor });

	return (
		<Popover
			defaultOpened={defaultOpened}
			position="bottom"
			withinPortal
			classNames={{ dropdown: classes.popoverDropdown }}
			disabled={readOnly}
		>
			<Popover.Target>
				<UnstyledButton sx={{ width: iconSize, height: iconSize }}>
					<Center className={classes.center}>
						{isEmoji(emojiIconValue) ? (
							<Text className={classes.emoji}>{selectedEmoji}</Text>
						) : (
							<Icon
								name={selectedIcon}
								className={classes.icon}
								iconWidth={iconSize}
								iconHeight={iconSize}
								iconPadding={0}
								size={iconSize >= 32 ? 'lg' : 'md'}
							/>
						)}
					</Center>
				</UnstyledButton>
			</Popover.Target>
			<Popover.Dropdown>
				<Box>
					<Tabs color="dark" value={activeTab} onTabChange={setActiveTab}>
						<Tabs.List>
							<Tabs.Tab value="emoji">Emojis</Tabs.Tab>
							<Tabs.Tab value="icon">Icons</Tabs.Tab>
							<Space className={classes.spacer} />
							<Button
								className={classes.removeButton}
								variant="tertiary"
								onClick={handleReset}
							>
								Reset
							</Button>
						</Tabs.List>

						<Tabs.Panel value="emoji" pt="sm">
							<Picker
								data={data}
								autoFocus
								onEmojiSelect={handleEmojiSelect}
								searchable
								size="small"
								emojiSize={20}
								previewEmoji={selectedEmoji}
								theme={colorScheme}
								navPosition="none"
								previewPosition="none"
								skinTonePosition="none"
							/>
						</Tabs.Panel>

						<Tabs.Panel value="icon" pt="sm">
							<IconSelector
								value={{
									iconName: selectedIcon,
									iconColor: selectedIconColor,
								}}
								onSelect={handleIconSelect}
							/>
						</Tabs.Panel>
					</Tabs>
				</Box>
			</Popover.Dropdown>
		</Popover>
	);
}

export default memo(IconEmojiSelector);
